import React, { useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import * as Bixby from '$/core';
import * as Services from '$/services';
import { ReactComponent as PortalLinkIcon } from '$/assets/icons/PortalLinkIcon.svg';
import { AssignableUser, DataSiteEmailTrackingData } from '$/core/models';
import {
  WorkflowManagementCommonState,
  DataSiteEmailTrackingGroupedRecordStatus,
  WorkflowManagementUploadToPortalState,
  WmtTaskPriority,
} from '$/core/enums';
import DocsCellRenderer from './cell-renderers/DocsCellRenderer';
import ReceivedClientsCellRenderer from './cell-renderers/ReceivedClientsCellRenderer';
import MissingClientsCellRenderer from './cell-renderers/MissingClientsCellRenderer';
import TaskCellRenderer from './cell-renderers/TaskCellRenderer';
import MarkClientsAsReceivedModal from './MarkClientsAsReceivedModal';
import CreateTaskModal from './task-modals/CreateTaskModal';

interface WorkflowGridProps {
  filteredTasks: DataSiteEmailTrackingData[];
  assigneeList?: AssignableUser[];
  dirtyRows: Map<string, DataSiteEmailTrackingGroupedRecordStatus>;
  onStatusChange: (groupId: string, newStatus: DataSiteEmailTrackingGroupedRecordStatus) => void;
  onDueDateChange: (groupId: string, dueDate: string | null) => void;
  onRefreshData: () => Promise<void>;
  dispatchToastError: (message: string) => void;
  dispatchToastSuccess: (message: string) => void;
  onPriorityChange: (groupId: string, priority: WmtTaskPriority) => void;
  dispatchToastInfo: (message: string) => void;
}

const WorkflowGrid: React.FC<WorkflowGridProps> = ({
  filteredTasks,
  assigneeList,
  dirtyRows,
  onStatusChange,
  onDueDateChange,
  onPriorityChange,
  onRefreshData,
  dispatchToastError,
  dispatchToastSuccess,
  dispatchToastInfo,
}) => {
  const gridApiRef = useRef<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<DataSiteEmailTrackingData | null>(null);
  const [createTaskModalOpen, setCreateTaskModalOpen] = useState(false);
  const [createTaskTarget, setCreateTaskTarget] = useState<DataSiteEmailTrackingData | null>(null);

  // Trigger row refresh when dirtyRows changes to update row classes
  useEffect(() => {
    if (gridApiRef.current) {
      gridApiRef.current.redrawRows();
    }
  }, [dirtyRows]);

  const handleOpenMarkAsReceivedModal = (taskData: DataSiteEmailTrackingData) => {
    setSelectedTask(taskData);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedTask(null);
  };

  const handleOpenCreateTaskModal = (taskData: DataSiteEmailTrackingData) => {
    setCreateTaskTarget(taskData);
    setCreateTaskModalOpen(true);
  };

  const handleCloseCreateTaskModal = () => {
    setCreateTaskModalOpen(false);
    setCreateTaskTarget(null);
  };

  const handleTaskCreated = async (
    _assigneeId: string | null,
    dueDate: string | null,
    priority: WmtTaskPriority,
    _note?: string
  ) => {
    // `_note` will be sent to the backend when the API is ready (BIXS-3198)
    if (createTaskTarget) {
      onDueDateChange(createTaskTarget.groupId, dueDate);
      onPriorityChange(createTaskTarget.groupId, priority);
    }
    dispatchToastSuccess('Task created successfully');
    dispatchToastInfo('Fetching updated rows...');
    await onRefreshData();
  };

  const handleMarkClientsAsReceived = async (clientIds: string[], onStartFetching: () => void) => {
    if (!selectedTask) return;

    try {
      await Services.DataSiteService.markClientsAsReceived(selectedTask.groupId, clientIds);
      dispatchToastSuccess(`Successfully marked ${clientIds.length} client(s) as received`);
      dispatchToastInfo('Fetching updated rows...');
      onStartFetching();
      await onRefreshData();
    } catch (error) {
      dispatchToastError('Failed to mark clients as received. Please try again.');
      throw error;
    }
  };

  const getContextMenuItems = (params: any) => {
    if (params.column.colId === 'name') {
      return [];
    }
    return ['copy', 'copyWithHeaders', 'separator', 'export'];
  };

  const linkCellFormatter = (params: any) => {
    const url = `/admin/company/${params.data['companyId']}/document-hosting/upload/${params.data['emailId'] ?? ''}`;
    const isEmailIdExists = !Bixby.Utils.isNullOrEmpty(params.data['emailId']);
    const isClientNameExists = !Bixby.Utils.isNullOrEmpty(params.data['clientName']);
    const isSyndtrakProvider = params.data['provider']?.toLowerCase() === Bixby.Constants.DATASITEPROVDIER_SYNDTRAK;
    const hasMissingClients = params.data.missingClients && params.data.missingClients.length > 0;
    const isButtonDisabled = hasMissingClients || !isEmailIdExists || !isSyndtrakProvider;

    // Build tooltip with reasons for disabled state
    const disabledReasons: string[] = [];
    if (hasMissingClients) {
      disabledReasons.push('Has missing clients');
    }
    if (!isEmailIdExists && !isClientNameExists) {
      disabledReasons.push('Preferred Client is missing');
    }
    if (!isSyndtrakProvider) {
      disabledReasons.push('Provider is not Syndtrak');
    }
    const tooltipText = disabledReasons.length > 0 ? disabledReasons.join(', ') : '';

    const handleClick = () => {
      if (!isButtonDisabled) {
        window.location.href = url;
      }
    };

    return (
      <div className="workflow-management-view-portal">
        <PortalLinkIcon />
        <button
          className="workflow-management-upload-button"
          onClick={handleClick}
          disabled={isButtonDisabled}
          title={isButtonDisabled ? tooltipText : ''}>
          Upload To Portal
        </button>
      </div>
    );
  };

  const dateTimeCellRenderer = (params: any) => {
    const date = new Date(params.value);
    const dateStr = date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
    return (
      <div
        style={{
          lineHeight: '1.2',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          height: '100%',
        }}>
        <div>{dateStr}</div>
        <div style={{ fontSize: '0.85em' }}>{timeStr}</div>
      </div>
    );
  };

  const portalLinkCellRenderer = (params: any) => {
    return (
      <div className="workflow-management-view-portal">
        <PortalLinkIcon />
        <a href={params.value}>View Portal</a>
      </div>
    );
  };

  const priorityCellRenderer = (params: any) => {
    const priority = (params.value ?? WmtTaskPriority.Medium) as WmtTaskPriority;
    return <span className={`wmt-priority-badge wmt-priority-badge--${priority.toLowerCase()}`}>{priority}</span>;
  };

  const handleCellValueChanged = (params: any) => {
    if (params.colDef.field === 'status') {
      const groupId = params.data.groupId;
      const newStatus = params.newValue as DataSiteEmailTrackingGroupedRecordStatus;
      onStatusChange(groupId, newStatus);
    }
    if (params.colDef.field === 'dueDate') {
      const groupId = params.data.groupId;
      const newDueDate = (params.newValue as string) || null;
      onDueDateChange(groupId, newDueDate);
    }
    if (params.colDef.field === 'priority') {
      const groupId = params.data.groupId;
      const newPriority = (params.newValue ?? WmtTaskPriority.Medium) as WmtTaskPriority;
      onPriorityChange(groupId, newPriority);
    }
  };

  const onGridReady = (params: any) => {
    gridApiRef.current = params.api;
  };

  // Custom cell editor for due date using the native browser date picker
  const DueDateCellEditor = React.forwardRef((props: any, ref) => {
    const [value, setValue] = useState<string>(props.value ?? '');
    React.useImperativeHandle(ref, () => ({
      getValue: () => value || null,
    }));
    return (
      <input
        type="date"
        value={value}
        onChange={e => setValue(e.target.value)}
        style={{
          width: '100%',
          height: '100%',
          padding: '0 8px',
          border: 'none',
          outline: 'none',
          boxSizing: 'border-box' as const,
        }}
        autoFocus
      />
    );
  });

  const dueDateCellRenderer = (params: any) => {
    if (!params.value) return <span className="wmt-due-date wmt-due-date--none">—</span>;

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Parse as local date to avoid timezone shifts (value is YYYY-MM-DD)
    const [year, month, day] = (params.value as string).split('-').map(Number);
    const dueDate = new Date(year, month - 1, day);

    const diffDays = Math.floor((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

    let badgeClass = '';
    if (diffDays < 0) {
      badgeClass = 'wmt-due-date--overdue';
    } else if (diffDays === 0) {
      badgeClass = 'wmt-due-date--today';
    }

    const dateStr = dueDate.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    });

    return <span className={`wmt-due-date ${badgeClass}`}>{dateStr}</span>;
  };

  return (
    <div className="workflow_management_data_grid">
      <div className="grid_container">
        <div className="grid ag-theme-alpine">
          <AgGridReact
            enableRangeSelection={true}
            getContextMenuItems={getContextMenuItems}
            onCellValueChanged={handleCellValueChanged}
            onGridReady={onGridReady}
            rowClassRules={{
              'workflow-row-dirty': (params: any) => {
                return params.data && dirtyRows.has(params.data.groupId);
              },
            }}
            defaultColDef={{
              flex: 1,
              minWidth: 200,
              sortable: true,
              filter: 'agTextColumnFilter',
              cellClass: 'align-left',
            }}
            rowData={filteredTasks}
            suppressDragLeaveHidesColumns={true}
            suppressMovableColumns={true}
            animateRows={true}
            rowHeight={35}
            stopEditingWhenCellsLoseFocus={true}
            headerHeight={35}
            columnDefs={[
              {
                field: 'clientName',
                headerName: 'Client Name',
              },
              {
                field: 'dataSiteName',
                headerName: 'Data Site Name',
                cellClass: 'align-left',
              },
              { field: 'bixbyId', headerName: 'Bixby Id', sort: 'asc' },
              {
                field: 'provider',
                headerName: 'Provider',
                minWidth: 110,
              },
              {
                field: 'docCount',
                headerName: 'Doc Count',
                minWidth: 150,
                cellDataType: 'number',
                cellClass: 'align-center',
                hide: true,
              },
              {
                field: 'docNames',
                headerName: 'Documents',
                autoHeight: true,
                cellRenderer: DocsCellRenderer,
              },
              {
                field: 'newsType',
                headerName: 'News Type',
                minWidth: 120,
              },
              {
                field: 'receivedClients',
                headerName: 'Received Clients',
                width: 100,
                autoHeight: true,
                cellRenderer: ReceivedClientsCellRenderer,
              },
              {
                field: 'missingClients',
                headerName: 'Missing Clients',
                width: 100,
                autoHeight: true,
                cellRenderer: MissingClientsCellRenderer,
                cellRendererParams: {
                  onOpenMarkAsReceivedModal: handleOpenMarkAsReceivedModal,
                },
              },
              {
                field: 'emailReceivedDate',
                headerName: 'Email Received',
                minWidth: 120,
                autoHeight: true,
                cellDataType: 'dateTime',
                cellRenderer: dateTimeCellRenderer,
              },
              {
                field: 'assignTask',
                headerName: 'Task',
                minWidth: 180,
                cellRenderer: TaskCellRenderer,
                cellRendererParams: {
                  onCreateTask: handleOpenCreateTaskModal,
                },
              },
              {
                field: 'portalLink',
                headerName: 'Upload To Portal',
                width: 100,
                cellRenderer: linkCellFormatter,
              },
              {
                field: 'assignee',
                headerName: 'Assignee',
                width: 100,
                editable: true,
                floatingFilter: true,
                cellEditor: 'agSelectCellEditor',
                cellEditorParams: {
                  values: assigneeList?.map(_ => _.id) || [],
                },
                hide: true,
              },
              {
                field: 'uploadToPortal',
                headerName: 'Upload To Portal',
                width: 100,
                editable: true,
                cellEditor: 'agSelectCellEditor',
                cellEditorParams: {
                  values: Object.values(WorkflowManagementUploadToPortalState),
                },
                hide: true,
              },
              {
                field: 'transcription',
                headerName: 'Transcription',
                width: 100,
                editable: true,
                cellEditor: 'agSelectCellEditor',
                cellEditorParams: {
                  values: Object.values(WorkflowManagementCommonState),
                },
                hide: true,
              },
              {
                field: 'modelUpdate',
                headerName: 'Model Update',
                width: 100,
                editable: true,
                cellEditor: 'agSelectCellEditor',
                cellEditorParams: {
                  values: Object.values(WorkflowManagementCommonState),
                },
                hide: true,
              },
              {
                field: 'portalLink',
                headerName: 'Portal Link',
                width: 100,
                cellRenderer: portalLinkCellRenderer,
                hide: true,
              },
              {
                field: 'dueDate',
                headerName: 'Due Date',
                width: 150,
                editable: true,
                cellEditor: DueDateCellEditor,
                cellRenderer: dueDateCellRenderer,
              },
              {
                field: 'priority',
                headerName: 'Priority',
                width: 130,
                editable: true,
                cellEditor: 'agSelectCellEditor',
                cellEditorParams: {
                  values: Object.values(WmtTaskPriority),
                },
                cellRenderer: priorityCellRenderer,
                valueFormatter: (params: any) => params.value ?? WmtTaskPriority.Medium,
              },
              {
                field: 'status',
                headerName: 'Status',
                width: 100,
                editable: true,
                cellEditor: 'agSelectCellEditor',
                floatingFilter: true,
                cellEditorParams: {
                  values: Object.values(DataSiteEmailTrackingGroupedRecordStatus),
                },
              },
              {
                field: 'completedAt',
                headerName: 'Completed At',
                width: 100,
                hide: true,
              },
            ]}
          />
        </div>
      </div>
      {selectedTask && (
        <MarkClientsAsReceivedModal
          isOpen={modalOpen}
          onClose={handleCloseModal}
          onMarkAsReceived={handleMarkClientsAsReceived}
          bixbyId={selectedTask.bixbyId}
          dataSiteName={selectedTask.dataSiteName}
          provider={selectedTask.provider}
          docNames={selectedTask.docNames}
          missingClients={selectedTask.missingClients}
        />
      )}
      <CreateTaskModal
        isOpen={createTaskModalOpen}
        taskData={createTaskTarget}
        assigneeList={assigneeList}
        onClose={handleCloseCreateTaskModal}
        onTaskCreated={handleTaskCreated}
      />
    </div>
  );
};

export default WorkflowGrid;
